import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MenuToggleIcon } from './ui/menu-toggle-icon';
import { HomeIcon, ShoppingCartIcon, UsersIcon, ArrowRightOnRectangleIcon, TicketIcon } from './icons';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/theme/hooks/useTheme';

const SunIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
		<circle cx="12" cy="12" r="4"></circle>
		<path d="M12 2v2"></path>
		<path d="M12 20v2"></path>
		<path d="m4.93 4.93 1.41 1.41"></path>
		<path d="m17.66 17.66 1.41 1.41"></path>
		<path d="M2 12h2"></path>
		<path d="M20 12h2"></path>
		<path d="m6.34 17.66-1.41 1.41"></path>
		<path d="m19.07 4.93-1.41 1.41"></path>
	</svg>
);

const MoonIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
		<path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"></path>
	</svg>
);

interface HamburgerMenuProps {
	clubHandle: string | null;
	isAuthenticated?: boolean;
}

export const HamburgerMenu: React.FC<HamburgerMenuProps> = ({ clubHandle, isAuthenticated = false }) => {
	const [isOpen, setIsOpen] = React.useState(false);
	const navigate = useNavigate();
	const location = useLocation();
	const { theme, toggleTheme } = useTheme();
	const { isGuest, user, isAuthenticated: authIsAuthenticated, isLoading: authLoading } = useAuth();

	const isRealUser = !authLoading && (isAuthenticated || authIsAuthenticated) && !isGuest && !user?.is_guest;

	// Close menu when route changes
	React.useEffect(() => {
		setIsOpen(false);
	}, [location.pathname]);

	const menuItems = [
		{ label: 'Home', path: clubHandle ? `/venues/${clubHandle}` : '/', show: true, icon: HomeIcon, action: undefined },
		{ label: 'Cart', path: '/carts', show: true, icon: ShoppingCartIcon, action: undefined },
		{ label: 'My Tickets', path: '/tickets', show: isRealUser, icon: TicketIcon, action: undefined },
		...(isRealUser 
			? [{ label: 'Profile', path: '/profile', show: true, icon: UsersIcon, action: undefined }]
			: [{ label: 'Login', path: '/login', show: true, icon: ArrowRightOnRectangleIcon, action: undefined }]
		),
		{ 
			label: theme === 'dark' ? 'Light Mode' : 'Dark Mode', 
			path: null, 
			show: true, 
			icon: theme === 'dark' ? SunIcon : MoonIcon,
			action: toggleTheme
		},
	].filter(item => item.show);


	const handleNavigation = (path: string) => {
		if (path) {
			setIsOpen(false);
			// Start transition prevents the navigation from blocking the menu's closing animation
			React.startTransition(() => {
				navigate(path);
			});
		}
	};

	const toggleMenu = () => {
		setIsOpen(!isOpen);
	};

	const closeMenu = () => {
		setIsOpen(false);
	};

	return (
		<>
			{/* Hamburger Button */}
			<button
				onClick={toggleMenu}
				className="fixed top-6 right-6 z-[100] p-3 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all duration-200 group"
				aria-label="Toggle menu"
				aria-expanded={isOpen}
			>
				<MenuToggleIcon open={isOpen} className="w-8 h-8" duration={500} />
			</button>

			{/* Backdrop */}
			<AnimatePresence>
				{isOpen && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.3 }}
						onClick={closeMenu}
						className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[90]"
					/>
				)}
			</AnimatePresence>

			{/* Menu Panel */}
			<AnimatePresence>
				{isOpen && (
					<motion.nav
						initial={{ x: 320, opacity: 0 }}
						animate={{ x: 0, opacity: 1 }}
						exit={{ x: 320, opacity: 0 }}
						transition={{
							type: 'spring',
							stiffness: 300,
							damping: 30,
						}}
						className="fixed top-0 right-0 h-full w-80 bg-black/95 backdrop-blur-xl border-l border-gray-800/50 z-[95] shadow-2xl"
						aria-label="Navigation menu"
					>
						<div className="flex flex-col h-full pt-20 px-6">
							{/* Menu Items */}
							<motion.ul
								initial="closed"
								animate="open"
								variants={{
									open: {
										transition: {
											staggerChildren: 0.1,
											delayChildren: 0.2,
										},
									},
									closed: {
										transition: {
											staggerChildren: 0.05,
											staggerDirection: -1,
										},
									},
								}}
								className="space-y-2"
							>
								{menuItems.map((item, index) => {
									const IconComponent = item.icon;
									return (
										<motion.li
											key={item.path || index}
											variants={{
												open: {
													opacity: 1,
													x: 0,
													transition: {
														type: 'spring',
														stiffness: 300,
														damping: 30,
													},
												},
												closed: {
													opacity: 0,
													x: 20,
													transition: {
														duration: 0.2,
													},
												},
											}}
										>
											<button
												onClick={() => {
													if (item.action) {
														item.action();
													} else if (item.path) {
														handleNavigation(item.path);
													}
												}}
												disabled={!item.path && !item.action}
												className="w-full flex items-center gap-3 px-4 py-3 text-white text-lg font-medium rounded-lg hover:bg-white/10 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
											>
												<IconComponent className="w-5 h-5 flex-shrink-0" />
												<span>{item.label}</span>
											</button>
										</motion.li>
									);
								})}
							</motion.ul>
						</div>
					</motion.nav>
				)}
			</AnimatePresence>
		</>
	);
};

export default HamburgerMenu;

